import type {DashboardTicket} from '../types/dashboardTicket';
import type {DashboardColumnKey} from '../constants/dashboardColumns';
import {PRIORITY_LABELS} from '../../tickets/constants/priority';
import {CHANNEL_LABELS} from '../../tickets/constants/channels';
import {STATUS_LABELS} from '../../tickets/components/TicketStatusBadge';
import {formatDateTime} from '../../tickets/utils/formatDate';
import {formatSlaDuration, slaPresentation} from '../../tickets/utils/slaPresentation';

/**
 * A dashboard ticket flattened for the design-system table, which reads each cell
 * through a plain accessor key. Every displayed string is derived here rather than
 * in a cell renderer, so the table, its sort and the CSV export all see the same
 * text the reader does.
 */
export interface DashboardTableRow {
	rowId: string;
	slug: string;
	subject: string;
	requesterName: string;
	contactName: string;
	assigneeName: string;
	statusLabel: string;
	priorityLabel: string;
	channelLabel: string;
	projectLabel: string;
	tagsText: string;
	created: string;
	slaStateText: string;
	slaDueAt: string;
	slaBreachedAt: string;
	slaPausedAt: string;
	resolutionTime: string;
	firstResponseTime: string;
	lastActivityAt: string;
	resolvedAt: string;
	timeInCurrentStatus: string;
	slaPolicyName: string;
	watchersCount: string;
	ticket: DashboardTicket;
}

/** The cell-bearing fields of a row — everything but its identity and its source ticket. */
export type DashboardTableCellKey = Exclude<keyof DashboardTableRow, 'rowId' | 'ticket'>;

/**
 * Which row field each column reads.
 *
 * One map rather than a literal string at every column and again in the export: the
 * table's `accessorKey` and the CSV's value have to come from the same field, or a
 * renamed row field silently empties one of the two.
 */
export const DASHBOARD_CELL_ACCESSOR: Readonly<Record<DashboardColumnKey, DashboardTableCellKey>> = {
	id: 'slug',
	subject: 'subject',
	requester: 'requesterName',
	contact: 'contactName',
	assignee: 'assigneeName',
	status: 'statusLabel',
	priority: 'priorityLabel',
	channel: 'channelLabel',
	project: 'projectLabel',
	tags: 'tagsText',
	created: 'created',
	sla_state: 'slaStateText',
	sla_due_at: 'slaDueAt',
	sla_breached_at: 'slaBreachedAt',
	sla_paused_at: 'slaPausedAt',
	resolution_time: 'resolutionTime',
	first_response_time: 'firstResponseTime',
	last_activity_at: 'lastActivityAt',
	resolved_at: 'resolvedAt',
	time_in_current_status: 'timeInCurrentStatus',
	sla_policy_name: 'slaPolicyName',
	watchers_count: 'watchersCount',
};

const EM_DASH = '—';

/** How many tag names read inline before the rest collapse to a `+N`. */
const TAGS_INLINE_LIMIT = 2;

/**
 * Tag names as one cell: up to two inline, then `+N` for the remainder (PRD §8.1
 * row 10). The design system's table renders text through a plain accessor, so the
 * PRD's chips would have to be drawn beside the text rather than instead of it —
 * which reads as each tag twice. The joined form is what the queue's Labels column
 * already does.
 */
export function truncateLabels(names: readonly string[]): string {
	if (names.length === 0) return EM_DASH;
	if (names.length <= TAGS_INLINE_LIMIT) return names.join(', ');
	return `${names.slice(0, TAGS_INLINE_LIMIT).join(', ')} +${names.length - TAGS_INLINE_LIMIT}`;
}

function formatSeconds(seconds: number | null | undefined): string {
	return seconds != null ? formatSlaDuration(seconds) : EM_DASH;
}

export function dashboardTableRows(tickets: readonly DashboardTicket[]): DashboardTableRow[] {
	return tickets.map(ticket => {
		const sla = slaPresentation(ticket.sla);
		return {
			rowId: ticket.id,
			slug: ticket.slug,
			subject: ticket.subject,
			requesterName: ticket.requester_name ?? EM_DASH,
			contactName: ticket.contact_name ?? EM_DASH,
			// "Unassigned", not a dash: an unassigned ticket is a state someone has to
			// act on, where a dash reads as "this field doesn't apply". Same wording as
			// the queue's Assignee column, so the two tables agree.
			assigneeName: ticket.assignee_name ?? 'Unassigned',
			statusLabel: STATUS_LABELS[ticket.status] ?? ticket.status,
			priorityLabel: PRIORITY_LABELS[ticket.priority] ?? EM_DASH,
			channelLabel: CHANNEL_LABELS[ticket.channel] ?? EM_DASH,
			projectLabel: ticket.project?.name ?? EM_DASH,
			tagsText: truncateLabels(ticket.labels.map(label => label.name)),
			// Absolute date and time, not a "1w ago" relative reading. The dashboard
			// is a scan across many tickets over a chosen period, where two rows
			// showing "1w ago" say only that both are the same age; the absolute
			// date says which came first.
			created: formatDateTime(ticket.created_at),
			slaStateText: sla.text,
			// Absolute, not relative: an SLA due moment is usually in the future, where
			// the relative formatters collapse to "just now". Breach and pause moments
			// read the same way, like every other timestamp column on the dashboard.
			slaDueAt: formatDateTime(ticket.sla?.due_at),
			slaBreachedAt: formatDateTime(ticket.sla?.breached_at),
			slaPausedAt: formatDateTime(ticket.sla?.paused_at),
			resolutionTime: formatSeconds(ticket.resolution_time_seconds),
			firstResponseTime: formatSeconds(ticket.first_response_time_seconds),
			lastActivityAt: formatDateTime(ticket.last_activity_at),
			resolvedAt: formatDateTime(ticket.resolved_at),
			timeInCurrentStatus: formatSeconds(ticket.time_in_current_status_seconds),
			slaPolicyName: ticket.sla_policy_name ?? EM_DASH,
			watchersCount: ticket.watchers_count != null ? String(ticket.watchers_count) : EM_DASH,
			ticket,
		};
	});
}
